import React, {useCallback, useMemo} from 'react';
import styled from 'styled-components';
import {useFormikContext} from 'formik';
import excel from 'shared/Excel';
import {useNotificationContext, useProductContext} from 'contexts';
import {Message} from 'contexts/NotificationContext';
import {validationErrorAppliesToField} from 'shared/validator';
import {
    Drawer,
    DrawerFields,
    HardwareType,
} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {
    FaceRange,
    shortRunner,
    stageHeight,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';
import {tint} from 'theme/helper';
import {
    FaceEdge,
    useFaceDrag,
} from 'components/customer/Product/Drawer/helpers/useFaceDrag';
import {FaceHeightInput} from 'components/customer/Product/Drawer/FaceHeightInput';
import {
    Grip,
    RAIL_WIDTH,
    Rail,
    RailEnd,
    RailLabel,
    RailLine,
} from 'components/customer/Product/Drawer/layoutStyles';

const RUNNER_FIELD = 'drawer_runner_specs';
const DRAWER_FIELDS = [RUNNER_FIELD, 'drawer_face_height'];

interface Face {
    index: number;
    hasError: boolean;
    hasRunnerError: boolean;
    height: number;
    isAuto: boolean;
    isLocked: boolean;
    runner: string;
    offset: number;
    pixels: number;
    isRoomy: boolean;
}

interface Handle {
    edge: FaceEdge;
    offset: number;
}

interface Carcase {
    faces: Face[];
    // how tall the cabinet is drawn
    box: number;
    ratio: number;
    total: number;
    margins: {
        top: number;
        bottom: number;
        left: number;
        right: number;
        gap: number;
    };
    insets: {left: number; right: number};
    pad: {top: number; bottom: number; left: number; right: number};
    totalLabel: string;
}

interface DrawerCarcaseProps {
    drawerParams: DrawerParameters;
    selectedIndex: number;
    onSelect: (index: number) => void;
    rangeFor: (index: number) => FaceRange;
}

const BOX_WIDTH = 300;
// folded, the drawing takes what the panel leaves, between these
const COMPACT_STAGE = 180;
const COMPACT_STAGE_MAX = 400;
const BOX_BORDER = 3;
// the left gutter holds the dimension rail; the other sides reserve room
// only for an overhang, which keeps the carcase aligned with the panel
const LEFT_PAD = RAIL_WIDTH + 4;
const HANDLE_HEIGHT = 14;
// keeps a very shallow face from vanishing
const MIN_FACE_PIXELS = 14;
const ROOMY_PIXELS = 34;
// stacked they need more room than the badge alone
const STACKED_PIXELS = 46;

const format = (value: number) => String(Math.round(value * 10) / 10);

// the name outlives the runner: DrawerRunner drops the hardware id when the
// stored runner is gone, so an entry without one means nothing is selected
const runnerLooksResolved = (drawer: Drawer) => {
    const entries = (drawer.hardware ?? []).filter(
        (item) => item?.hardware?.type === HardwareType.RUNNER
    );

    if (entries.length === 0) return true;

    return entries.some((item) => !!item.hardware_id);
};

// The cabinet drawn to scale, margins and gaps included. A negative margin is
// an overhang and is drawn outside the carcase rather than clamped away.
export const DrawerCarcase = ({
    drawerParams,
    selectedIndex,
    onSelect,
    rangeFor,
}: DrawerCarcaseProps) => {
    const {values} = useFormikContext<DrawerFields>();
    const {messages} = useNotificationContext();
    const {getMaterialOptions} = useProductContext<{
        getMaterialOptions: () => object;
    }>();

    const selectHandler = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            onSelect(Number(event.currentTarget.dataset.index));
        },
        [onSelect]
    );

    const erroredDrawers = useMemo(() => {
        const errors = messages?.errors;
        // errors arrive either as a flat list or keyed by drawer index
        const list: Message[] = Array.isArray(errors)
            ? errors
            : Object.values(errors ?? {}).flat<Message[][]>();

        return (values.drawers ?? []).map((drawer, index) => ({
            any: list.some((error) =>
                DRAWER_FIELDS.some((field) =>
                    validationErrorAppliesToField(error, field, index)
                )
            ),
            runner: list.some((error) =>
                validationErrorAppliesToField(error, RUNNER_FIELD, index)
            ),
        }));
    }, [messages, values.drawers]);

    const compact = useCompactLayout();
    const {faces, box, ratio, total, margins, insets, pad, totalLabel} =
        useMemo<Carcase>(() => {
            const drawers = values.drawers ?? [];
            const total = excel.calculate<number>(
                drawerParams.totalDrawerHeight,
                {
                    ...values,
                    ...getMaterialOptions(),
                }
            );

            const empty = {
                faces: [] as Face[],
                box: stageHeight(0, compact),
                ratio: 1,
                total: 0,
                margins: {top: 0, bottom: 0, left: 0, right: 0, gap: 0},
                insets: {left: 0, right: 0},
                pad: {top: 0, bottom: 0, left: LEFT_PAD, right: 0},
                totalLabel: '',
            };

            if (!(total > 0) || drawers.length === 0) return empty;

            const marginAt = (fieldName: string) =>
                toNumber(values[String(fieldName)]);

            const top = marginAt(drawerParams.drawerTopMarginFieldName);
            const bottom = marginAt(drawerParams.drawerBottomMarginFieldName);
            const left = marginAt(drawerParams.drawerLeftMarginFieldName);
            const right = marginAt(drawerParams.drawerRightMarginFieldName);
            const gap = marginAt(drawerParams.drawerGapFieldName);

            // px per mm of cabinet height, and the only scale in the drawing
            const box = stageHeight(total, compact);
            const ratio = box / total;

            // the last auto drawer absorbs the leftover, so it cannot be fixed
            const autoCount = drawers.filter(
                (drawer) => drawer.drawer_style == 1
            ).length;

            let cursor = top;

            // an overhang reserves exactly its own depth, nothing otherwise
            const overhang = (margin: number) =>
                margin < 0 ? Math.ceil(-margin * ratio) : 0;

            return {
                box,
                ratio,
                total,
                margins: {top, bottom, left, right, gap},
                insets: {left: left * ratio, right: right * ratio},
                pad: {
                    top: overhang(top),
                    bottom: overhang(bottom),
                    left: Math.max(LEFT_PAD, overhang(left)),
                    right: overhang(right),
                },
                totalLabel: format(total),
                faces: drawers.map((drawer, index) => {
                    const height = toNumber(drawer.drawer_face_height);
                    const offset = cursor;
                    const pixels = Math.max(MIN_FACE_PIXELS, height * ratio);

                    cursor += height + gap;

                    return {
                        index,
                        height,
                        offset: offset * ratio,
                        pixels,
                        isRoomy: pixels >= ROOMY_PIXELS,
                        isAuto: drawer.drawer_style == 1,
                        hasError: erroredDrawers[Number(index)]?.any === true,
                        // a short face has no room for the tag below, so only
                        // the border can report a face with no runner
                        hasRunnerError:
                            erroredDrawers[Number(index)]?.runner === true ||
                            !runnerLooksResolved(drawer) ||
                            shortRunner(drawer.drawer_runner_specs) === '',
                        isLocked: drawer.drawer_style == 1 && autoCount <= 1,
                        runner: shortRunner(drawer.drawer_runner_specs),
                    };
                }),
            };
        }, [values, drawerParams, getMaterialOptions, erroredDrawers, compact]);

    const {dragging, preview, startDrag, nudge, setHeight} = useFaceDrag({
        faceHeightFieldName: drawerParams.drawerFaceHeightFieldName,
        styleFieldName: drawerParams.drawerStyleFieldName,
        ratio,
        total,
        top: margins.top,
        bottom: margins.bottom,
        gap: margins.gap,
        rangeFor,
    });

    // a drag is previewed locally until it ends; positions shift too
    const shownFaces = useMemo(() => {
        if (preview === null) return faces;

        let cursor = margins.top;

        // the dragged drawer is the one that ends up fixed
        return faces.map((face) => {
            const height = preview.heights[Number(face.index)] ?? face.height;
            const offset = cursor;
            const pixels = Math.max(MIN_FACE_PIXELS, height * ratio);

            cursor += height + margins.gap;

            return {
                ...face,
                height,
                offset: offset * ratio,
                pixels,
                isRoomy: pixels >= ROOMY_PIXELS,
                isAuto: face.isAuto && face.index !== preview.dragged,
            };
        });
    }, [faces, preview, ratio, margins]);

    // handles overlay the faces, so the drawn gap stays the real margin
    const handles = useMemo<Handle[]>(() => {
        const face = shownFaces[Number(selectedIndex)];

        if (!face || face.isLocked) return [];

        return [
            ...(selectedIndex > 0
                ? [{edge: 'top' as FaceEdge, offset: face.offset}]
                : []),
            ...(selectedIndex < shownFaces.length - 1
                ? [
                      {
                          edge: 'bottom' as FaceEdge,
                          offset: face.offset + face.pixels,
                      },
                  ]
                : []),
        ];
    }, [shownFaces, selectedIndex]);

    return (
        <Stage
            data-cy="drawer-stage"
            $compact={compact}
            style={{
                flexBasis: compact
                    ? COMPACT_STAGE
                    : pad.left + BOX_WIDTH + pad.right,
                maxWidth: compact
                    ? COMPACT_STAGE_MAX
                    : pad.left + BOX_WIDTH + pad.right,
                height: pad.top + box + pad.bottom,
            }}>
            <Rail
                style={{top: pad.top, height: box}}
                data-cy="drawer-dimension"
                title="Total drawer height"
                aria-label={`Total drawer height ${totalLabel} mm`}>
                <RailEnd data-cy="drawer-dimension-start" />
                <RailLine />
                <RailLabel>{totalLabel} mm</RailLabel>
                <RailLine />
                <RailEnd data-cy="drawer-dimension-end" />
            </Rail>

            <CarcaseBox
                data-cy="drawer-carcase"
                style={{
                    top: pad.top,
                    left: pad.left,
                    right: pad.right,
                    height: box,
                }}
            />

            {shownFaces.map((face) => (
                <Face
                    key={face.index}
                    data-index={face.index}
                    data-cy={`drawer-face-${face.index}`}
                    onClick={selectHandler}
                    $selected={face.index === selectedIndex}
                    $auto={face.isAuto}
                    $error={face.hasError || face.hasRunnerError}
                    data-error={
                        face.hasError || face.hasRunnerError ? 'true' : 'false'
                    }
                    style={{
                        top: pad.top + face.offset,
                        left: pad.left + insets.left,
                        right: pad.right + insets.right,
                        height: face.pixels,
                    }}
                    title={`Drawer ${face.index + 1}`}>
                    <FaceHeightInput
                        runnerIndex={face.index}
                        value={face.height}
                        pixels={face.pixels}
                        disabled={face.isLocked}
                        onSet={setHeight}
                    />
                    <Spacer />
                    {face.isRoomy ? (
                        <Meta
                            $compact={compact}
                            data-stacked={compact ? 'true' : 'false'}>
                            {face.isAuto ? <AutoBadge>AUTO</AutoBadge> : null}
                            {face.pixels >= STACKED_PIXELS || !compact ? (
                                <RunnerTag
                                    data-cy={`runner-code-${face.index}`}
                                    title={
                                        face.runner === '' ||
                                        face.hasRunnerError
                                            ? 'No runner fits this face height'
                                            : undefined
                                    }>
                                    {face.runner === '' || face.hasRunnerError
                                        ? 'No runner'
                                        : face.runner}
                                </RunnerTag>
                            ) : null}
                        </Meta>
                    ) : null}
                </Face>
            ))}

            {handles.map((handle) => (
                <Handle
                    key={handle.edge}
                    data-index={selectedIndex}
                    data-edge={handle.edge}
                    data-cy={`drawer-handle-${handle.edge}`}
                    onPointerDown={startDrag}
                    onKeyDown={nudge}
                    tabIndex={0}
                    role="separator"
                    aria-label={`${
                        handle.edge === 'top'
                            ? 'Raise or lower the top'
                            : 'Raise or lower the bottom'
                    } of drawer ${selectedIndex + 1}`}
                    style={{
                        top: pad.top + handle.offset - HANDLE_HEIGHT / 2,
                        left: pad.left + insets.left,
                        right: pad.right + insets.right,
                    }}
                    $dragging={dragging === handle.edge}>
                    <Grip $dragging={dragging === handle.edge} />
                </Handle>
            ))}
        </Stage>
    );
};

const Stage = styled.div<{$compact: boolean}>`
    position: relative;
    /* shrinks before the panel does; folded, it takes what the panel leaves */
    flex-grow: ${({$compact}) => ($compact ? 1 : 0)};
    flex-shrink: 1;
    min-width: 0;
    box-sizing: content-box;
`;

// folded there is no room beside the height, so these stack instead
const Meta = styled.span<{$compact: boolean}>`
    flex: none;
    display: flex;
    align-items: ${({$compact}) => ($compact ? 'flex-end' : 'center')};
    flex-direction: ${({$compact}) => ($compact ? 'column' : 'row')};
    gap: ${({$compact}) => ($compact ? '2px' : '8px')};
    min-width: 0;
`;

const CarcaseBox = styled.div`
    position: absolute;
    box-sizing: border-box;
    background: ${({theme}) => tint(theme.colors.primary?.main, 0.08)};
    border: ${BOX_BORDER}px solid
        ${({theme}) => tint(theme.colors.primary?.main, 0.35)};
    border-radius: 4px;
    box-shadow: inset 0 0 0 1px #fff;
`;

const Handle = styled.div<{$dragging: boolean}>`
    /* without this the browser claims the gesture for scrolling on touch */
    touch-action: none;
    position: absolute;
    z-index: 4;
    box-sizing: border-box;
    height: ${HANDLE_HEIGHT}px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ns-resize;
    outline: none;
    background: transparent;

    &:focus-visible span {
        box-shadow: 0 0 0 2px ${({theme}) => theme.colors.primary?.main};
    }
`;

const Face = styled.div<{
    $selected: boolean;
    $auto: boolean;
    $error: boolean;
}>`
    position: absolute;
    box-sizing: border-box;
    z-index: ${({$selected}) => ($selected ? 2 : 1)};
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    cursor: pointer;
    border-radius: 2px;
    overflow: hidden;
    background: ${({$selected, $auto, theme}) =>
        $selected
            ? '#fff'
            : $auto
              ? `repeating-linear-gradient(135deg, ${tint(
                    theme.colors.primary?.main,
                    0.05
                )} 0 9px, ${tint(theme.colors.primary?.main, 0.11)} 9px 18px)`
              : tint(theme.colors.primary?.main, 0.05)};
    border: ${({$selected, $auto, $error, theme}) =>
        $error
            ? `2px solid ${theme.colors.errorLabel?.main}`
            : $selected
              ? `2px solid ${theme.colors.primary?.main}`
              : `1px ${$auto ? 'dashed' : 'solid'} ${
                    theme.colors.primary?.main
                }`};
    box-shadow: ${({$selected, $error, theme}) =>
        $error
            ? `inset 0 0 0 1px ${theme.colors.error?.main}`
            : $selected
              ? `0 0 0 3px ${tint(theme.colors.primary?.main, 0.18)}`
              : `inset 0 -1px 0 ${tint(theme.colors.primary?.main, 0.06)}`};
`;

const Spacer = styled.span`
    flex: 1;
`;

const AutoBadge = styled.span`
    background: ${({theme}) => tint(theme.colors.primary?.main, 0.12)};
    border: 1px solid ${({theme}) => tint(theme.colors.primary?.main, 0.25)};
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.4px;
    white-space: nowrap;
    color: ${({theme}) => theme.colors.primary?.main};
`;

const RunnerTag = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: #8b97a6;
    border-left: 1px solid
        ${({theme}) => tint(theme.colors.primary?.main, 0.15)};
    padding-left: 8px;

    /* stacked it has a line of its own, so it needs neither */
    ${Meta}[data-stacked='true'] & {
        border-left: none;
        padding-left: 0;
        overflow: visible;
        text-overflow: clip;
    }
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    /* trims rather than vanishing if a face is narrower than its content */
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;
